// License: copied from https://github.com/tinymce/tinymce-react
import * as React from 'react';
import { ScriptLoader } from '../ScriptLoader2';
import { getTinymce } from '../TinyMCE';
import { configHandlers, isBeforeInputEventAvailable, isFunction, isInDoc, isTextareaOrInput, mergePlugins, setMode, uuid } from '../Utils';
import { EditorPropTypes } from './EditorPropTypes';
/**
 * @see {@link https://www.tiny.cloud/docs/tinymce/7/react-ref/} for the TinyMCE React Technical Reference
 */
export class Editor extends React.Component {
    /**
     * @param {Partial<IAllProps>} props - Component props, including editor configuration.
     */
    constructor(props) {
        var _a, _b, _c;
        super(props);
        this.rollbackTimer = undefined;
        this.valueCursor = undefined;
        this.rollbackChange = () => {
            const editor = this.editor;
            const value = this.props.value;
            if (editor && value && value !== this.currentContent) {
                editor.undoManager.ignore(() => {
                    editor.setContent(value);
                    // only restore cursor on inline editors when they are focused
                    // as otherwise it will cause a focus grab
                    if (this.valueCursor && (!this.inline || editor.hasFocus())) {
                        try {
                            editor.selection.moveToBookmark(this.valueCursor);
                        }
                        catch (e) {
                            /* ignore */
                        }
                    }
                });
            }
            this.rollbackTimer = undefined;
        };
        this.handleBeforeInput = (_evt) => {
            if (this.props.value !== undefined && this.props.value === this.currentContent && this.editor) {
                if (!this.inline || this.editor.hasFocus()) {
                    try {
                        // getBookmark throws exceptions when the editor has not been focused
                        // possibly only in inline mode but I'm not taking chances
                        this.valueCursor = this.editor.selection.getBookmark(3);
                    }
                    catch (e) {
                        /* ignore */
                    }
                }
            }
        };
        this.handleBeforeInputSpecial = (evt) => {
            if (evt.key === 'Enter' || evt.key === 'Backspace' || evt.key === 'Delete') {
                this.handleBeforeInput(evt);
            }
        };
        this.handleEditorChange = (_evt) => {
            const editor = this.editor;
            if (editor && editor.initialized) {
                const newContent = editor.getContent();
                if (this.props.value !== undefined && this.props.value !== newContent && this.props.rollback !== false) {
                    // start a timer and revert to the value if not applied in time
                    if (!this.rollbackTimer) {
                        this.rollbackTimer = window.setTimeout(this.rollbackChange, typeof this.props.rollback === 'number' ? this.props.rollback : 200);
                    }
                }
                if (newContent !== this.currentContent) {
                    this.currentContent = newContent;
                    if (isFunction(this.props.onEditorChange)) {
                        this.props.onEditorChange(newContent, editor);
                    }
                }
            }
        };
        this.handleEditorChangeSpecial = (evt) => {
            if (evt.key === 'Backspace' || evt.key === 'Delete') {
                this.handleEditorChange(evt);
            }
        };
        this.initialise = (attempts = 0) => {
            var _a, _b, _c;
            const target = this.elementRef.current;
            if (!target) {
                return; // Editor has been unmounted
            }
            if (!isInDoc(target)) {
                // this is probably someone trying to help by rendering us offscreen
                // but we can't do that because the editor iframe must be in the document
                // in order to have state
                if (attempts === 0) {
                    // we probably just need to wait for the current events to be processed
                    setTimeout(() => this.initialise(1), 1);
                }
                else if (attempts < 100) {
                    // wait for ten seconds, polling every tenth of a second
                    setTimeout(() => this.initialise(attempts + 1), 100);
                }
                else {
                    // give up, at this point it seems that more polling is unlikely to help
                    throw new Error('tinymce can only be initialised when in a document');
                }
                return;
            }
            const tinymce = getTinymce(this.view);
            if (!tinymce) {
                throw new Error('tinymce should have been loaded into global scope');
            }
            const finalInit = Object.assign(Object.assign(Object.assign(Object.assign({}, this.props.init), { selector: undefined, target, readonly: this.props.disabled, inline: this.inline, plugins: mergePlugins((_a = this.props.init) === null || _a === void 0 ? void 0 : _a.plugins, this.props.plugins), toolbar: (_b = this.props.toolbar) !== null && _b !== void 0 ? _b : (_c = this.props.init) === null || _c === void 0 ? void 0 : _c.toolbar }), (this.props.licenseKey ? { license_key: this.props.licenseKey } : {})), { setup: (editor) => {
                    this.editor = editor;
                    this.bindHandlers({});
                    // When running in inline mode the editor gets the initial value
                    // from the innerHTML of the element it is initialized on.
                    // However we don't want to take on the responsibility of sanitizing
                    // to remove XSS in the react integration so we have a chicken and egg
                    // problem... We avoid it by sneaking in a set content before the first
                    // "official" setContent and using TinyMCE to do the sanitization.
                    if (this.inline && !isTextareaOrInput(target)) {
                        editor.once('PostRender', (_evt) => {
                            editor.setContent(this.getInitialValue(), { no_events: true });
                        });
                    }
                    if (this.props.init && isFunction(this.props.init.setup)) {
                        this.props.init.setup(editor);
                    }
                }, init_instance_callback: (editor) => {
                    var _a, _b;
                    // check for changes that happened since tinymce.init() was called
                    const initialValue = this.getInitialValue();
                    this.currentContent = (_a = this.currentContent) !== null && _a !== void 0 ? _a : editor.getContent();
                    if (this.currentContent !== initialValue) {
                        this.currentContent = initialValue;
                        // same as resetContent in TinyMCE 5
                        editor.setContent(initialValue);
                        editor.undoManager.clear();
                        editor.undoManager.add();
                        editor.setDirty(false);
                    }
                    const disabled = (_b = this.props.disabled) !== null && _b !== void 0 ? _b : false;
                    setMode(this.editor, disabled ? 'readonly' : 'design');
                    // ensure existing init_instance_callback is called
                    if (this.props.init && isFunction(this.props.init.init_instance_callback)) {
                        this.props.init.init_instance_callback(editor);
                    }
                } });
            if (!this.inline) {
                target.style.visibility = '';
            }
            if (isTextareaOrInput(target)) {
                target.value = this.getInitialValue();
            }
            tinymce.init(finalInit);
        };
        this.id = this.props.id || uuid('tiny-react');
        this.elementRef = React.createRef();
        this.inline = (_c = (_a = this.props.inline) !== null && _a !== void 0 ? _a : (_b = this.props.init) === null || _b === void 0 ? void 0 : _b.inline) !== null && _c !== void 0 ? _c : false;
        this.boundHandlers = {};
    }
    get view() {
        var _a, _b;
        return (_b = (_a = this.elementRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.defaultView) !== null && _b !== void 0 ? _b : window;
    }
    /**
     * Updates the editor instance based on prop changes (like `value`, `disabled`, or `initialValue`).
     *
     * @param {Partial<IAllProps>} prevProps - The previous props before the update.
     */
    componentDidUpdate(prevProps) {
        var _a, _b;
        if (this.rollbackTimer) {
            clearTimeout(this.rollbackTimer);
            this.rollbackTimer = undefined;
        }
        if (this.editor) {
            this.bindHandlers(prevProps);
            if (this.editor.initialized) {
                this.currentContent = (_a = this.currentContent) !== null && _a !== void 0 ? _a : this.editor.getContent();
                if (typeof this.props.initialValue === 'string' && this.props.initialValue !== prevProps.initialValue) {
                    // same as resetContent in TinyMCE 5
                    this.editor.setContent(this.props.initialValue);
                    this.editor.undoManager.clear();
                    this.editor.undoManager.add();
                    this.editor.setDirty(false);
                }
                else if (typeof this.props.value === 'string' && this.props.value !== this.currentContent) {
                    const localEditor = this.editor;
                    localEditor.undoManager.transact(() => {
                        // inline editors grab focus when restoring selection
                        // so we don't try to keep their selection unless they are currently focused
                        let cursor;
                        if (!this.inline || localEditor.hasFocus()) {
                            try {
                                // getBookmark throws exceptions when the editor has not been focused
                                // possibly only in inline mode but I'm not taking chances
                                cursor = localEditor.selection.getBookmark(3);
                            }
                            catch (e) {
                                /* ignore */
                            }
                        }
                        const valueCursor = this.valueCursor;
                        localEditor.setContent(this.props.value);
                        if (!this.inline || localEditor.hasFocus()) {
                            for (const bookmark of [cursor, valueCursor]) {
                                if (bookmark) {
                                    try {
                                        localEditor.selection.moveToBookmark(bookmark);
                                        this.valueCursor = bookmark;
                                        break;
                                    }
                                    catch (e) {
                                        /* ignore */
                                    }
                                }
                            }
                        }
                    });
                }
                if (this.props.disabled !== prevProps.disabled) {
                    const disabled = (_b = this.props.disabled) !== null && _b !== void 0 ? _b : false;
                    setMode(this.editor, disabled ? 'readonly' : 'design');
                }
            }
        }
    }
    componentDidMount() {
        var _a, _b, _c, _d, _e;
        if (getTinymce(this.view) !== null) {
            this.initialise();
        }
        else if (Array.isArray(this.props.tinymceScriptSrc) && this.props.tinymceScriptSrc.length === 0) {
            (_b = (_a = this.props).onScriptsLoadError) === null || _b === void 0 ? void 0 : _b.call(_a, new Error('No `tinymce` global is present but the `tinymceScriptSrc` prop was an empty array.'));
        }
        else if ((_c = this.elementRef.current) === null || _c === void 0 ? void 0 : _c.ownerDocument) {
            const successHandler = () => {
                var _a, _b;
                (_b = (_a = this.props).onScriptsLoad) === null || _b === void 0 ? void 0 : _b.call(_a);
                this.initialise();
            };
            const errorHandler = (err) => {
                var _a, _b;
                (_b = (_a = this.props).onScriptsLoadError) === null || _b === void 0 ? void 0 : _b.call(_a, err);
            };
            ScriptLoader.loadList(this.elementRef.current.ownerDocument, this.getScriptSources(), (_e = (_d = this.props.scriptLoading) === null || _d === void 0 ? void 0 : _d.delay) !== null && _e !== void 0 ? _e : 0, successHandler, errorHandler);
        }
    }
    componentWillUnmount() {
        const editor = this.editor;
        if (editor) {
            editor.off(this.changeEvents(), this.handleEditorChange);
            editor.off(this.beforeInputEvent(), this.handleBeforeInput);
            editor.off('keypress', this.handleEditorChangeSpecial);
            editor.off('keydown', this.handleBeforeInputSpecial);
            editor.off('NewBlock', this.handleEditorChange);
            Object.keys(this.boundHandlers).forEach((eventName) => {
                editor.off(eventName, this.boundHandlers[eventName]);
            });
            this.boundHandlers = {};
            editor.remove();
            this.editor = undefined;
        }
    }
    /**
     * Renders the TinyMCE component based on `inline` mode.
     *
     * @returns {React.ReactNode} The rendered DOM element (div or textarea).
     */
    render() {
        return this.inline ? this.renderInline() : this.renderIframe();
    }
    changeEvents() {
        var _a, _b, _c;
        const isIE = (_c = (_b = (_a = getTinymce(this.view)) === null || _a === void 0 ? void 0 : _a.Env) === null || _b === void 0 ? void 0 : _b.browser) === null || _c === void 0 ? void 0 : _c.isIE();
        return isIE ? 'change keyup compositionend setcontent CommentChange' : 'change input compositionend setcontent CommentChange';
    }
    beforeInputEvent() {
        return isBeforeInputEventAvailable() ? 'beforeinput SelectionChange' : 'SelectionChange';
    }
    renderInline() {
        const { tagName = 'div' } = this.props;
        return React.createElement(tagName, {
            ref: this.elementRef,
            id: this.id,
            tabIndex: this.props.tabIndex,
        });
    }
    renderIframe() {
        return React.createElement('textarea', {
            ref: this.elementRef,
            style: { visibility: 'hidden' },
            name: this.props.textareaName,
            id: this.id,
            tabIndex: this.props.tabIndex,
        });
    }
    getScriptSources() {
        var _a, _b;
        const async = (_a = this.props.scriptLoading) === null || _a === void 0 ? void 0 : _a.async;
        const defer = (_b = this.props.scriptLoading) === null || _b === void 0 ? void 0 : _b.defer;
        if (this.props.tinymceScriptSrc !== undefined) {
            if (typeof this.props.tinymceScriptSrc === 'string') {
                return [{ src: this.props.tinymceScriptSrc, async, defer }];
            }
            // multiple scripts can be specified which allows for hybrid mode
            return this.props.tinymceScriptSrc.map((item) => {
                if (typeof item === 'string') {
                    // async does not make sense for multiple items unless
                    // they are not dependent (which will be unlikely)
                    return { src: item, async, defer };
                }
                else {
                    return item;
                }
            });
        }
        // fallback to the cloud when the tinymceScriptSrc is not specified
        const channel = this.props.cloudChannel; // `cloudChannel` is in `defaultProps`, so it's always defined.
        const apiKey = this.props.apiKey ? this.props.apiKey : 'no-api-key';
        const cloudTinyJs = `https://cdn.tiny.cloud/1/${apiKey}/tinymce/${channel}/tinymce.min.js`;
        return [{ src: cloudTinyJs, async, defer }];
    }
    getInitialValue() {
        if (typeof this.props.initialValue === 'string') {
            return this.props.initialValue;
        }
        else if (typeof this.props.value === 'string') {
            return this.props.value;
        }
        else {
            return '';
        }
    }
    bindHandlers(prevProps) {
        if (this.editor !== undefined) {
            // typescript chokes trying to understand the type of the lookup function
            configHandlers(this.editor, prevProps, this.props, this.boundHandlers, (key) => this.props[key]);
            // check if we should monitor editor changes
            const isValueControlled = (p) => p.onEditorChange !== undefined || p.value !== undefined;
            const wasControlled = isValueControlled(prevProps);
            const nowControlled = isValueControlled(this.props);
            if (!wasControlled && nowControlled) {
                this.editor.on(this.changeEvents(), this.handleEditorChange);
                this.editor.on(this.beforeInputEvent(), this.handleBeforeInput);
                this.editor.on('keydown', this.handleBeforeInputSpecial);
                this.editor.on('keyup', this.handleEditorChangeSpecial);
                this.editor.on('NewBlock', this.handleEditorChange);
            }
            else if (wasControlled && !nowControlled) {
                this.editor.off(this.changeEvents(), this.handleEditorChange);
                this.editor.off(this.beforeInputEvent(), this.handleBeforeInput);
                this.editor.off('keydown', this.handleBeforeInputSpecial);
                this.editor.off('keyup', this.handleEditorChangeSpecial);
                this.editor.off('NewBlock', this.handleEditorChange);
            }
        }
    }
}
Editor.propTypes = EditorPropTypes;
Editor.defaultProps = {
    cloudChannel: '7',
};
